/**
 * Global Context Bar
 *
 * Persistent selector for the Project > Run hierarchy. Retains selections as
 * the user navigates between pages and navigates to the chosen run's results.
 */

import { useNavigate } from 'react-router-dom';
import { Flex, useTheme, spacing } from '@fti/design-system';
import { useProjects } from '../../../api/media-sim/projects';
import { useRuns } from '../../../api/media-sim/runs';
import { useProjectRun } from './ProjectRunContext';

export function GlobalContextBar() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { selectedProjectId, selectedRunId, selectProject, selectRun } = useProjectRun();

  const { data: projects } = useProjects({ scope: 'all', limit: 1000 });
  // Runs load only once a project is selected.
  const { data: runs } = useRuns(selectedProjectId || '');

  const borderColor = theme.colors.border.default;
  const bgColor = theme.colors.background.paper;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const selectStyle: React.CSSProperties = {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: bgColor,
    color: textColor,
    fontSize: '0.825rem',
    minWidth: '200px',
    maxWidth: '260px',
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    selectProject(e.target.value || null);
  };

  const handleRunChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const runId = e.target.value || null;
    selectRun(runId);
  };

  const selectedProject = projects?.find((p) => p.id === selectedProjectId);
  const selectedRun = runs?.find((r) => r.id === selectedRunId);

  return (
    <div style={{
      padding: `${spacing.sm} ${spacing.lg}`,
      borderBottom: `1px solid ${borderColor}`,
      background: bgColor,
    }}>
      <Flex align="center" gap="md">
        <span style={{
          fontSize: '0.75rem',
          color: mutedColor,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Context
        </span>

        <select style={selectStyle} value={selectedProjectId || ''} onChange={handleProjectChange}>
          <option value="">Select Project...</option>
          {/* Keep the current selection visible even if the fetched list is stale. */}
          {selectedProjectId && !projects?.some((p) => p.id === selectedProjectId) && selectedProject && (
            <option value={selectedProjectId}>{selectedProject.project_name}</option>
          )}
          {projects?.map((p) => (
            <option key={p.id} value={p.id}>{p.project_name}</option>
          ))}
        </select>

        <span style={{ color: mutedColor }}>/</span>

        <select
          style={selectStyle}
          value={selectedRunId || ''}
          onChange={handleRunChange}
          disabled={!selectedProjectId}
        >
          <option value="">Select Run...</option>
          {selectedRunId && !runs?.some((r) => r.id === selectedRunId) && selectedRun && (
            <option value={selectedRunId}>{selectedRun.run_name || '(Untitled)'}</option>
          )}
          {runs?.map((r) => (
            <option key={r.id} value={r.id}>{r.run_name || '(Untitled)'}</option>
          ))}
        </select>

        {selectedProjectId && (
          <button
            onClick={() => navigate('/simulations/overview')}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[600],
              fontSize: '0.825rem',
              fontWeight: 500,
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            Open project →
          </button>
        )}
      </Flex>
    </div>
  );
}
